import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { BehaviorSubject } from "rxjs";

const shallowEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  if (aKeys.length !== bKeys.length) return false;

  return aKeys.every(key => Object.is(a[key], b[key]));
};

/**
 * Renders the React element emitted by an observable stream.
 *
 * Replacement for recompose's `componentFromStream`. The factory receives a
 * stream of props and returns a stream of elements, exactly as before.
 *
 * The subscription is created in a layout effect so that a stream which emits
 * synchronously on subscribe (as `withLoadingAndErrorHandling` does via
 * `startWith`) is reflected before the browser paints.
 */
export default function useStreamedElement(streamFactory, props) {
  const props$ = useRef(null);
  if (props$.current === null) {
    props$.current = new BehaviorSubject(props);
  }

  const [element, setElement] = useState(null);

  useLayoutEffect(() => {
    const subscription = streamFactory(props$.current).subscribe(setElement);

    return () => subscription.unsubscribe();
    // The factory is intentionally subscribed to once; new props are delivered
    // through props$ rather than by resubscribing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastProps = useRef(props);
  useEffect(() => {
    // Only forward genuinely new props. Emitting on every render would feed the
    // stream's own output back into it: the emitted element re-renders this
    // component, which would push props again and re-trigger the API calls.
    // This mirrors recompose's componentWillReceiveProps behaviour.
    if (shallowEqual(lastProps.current, props)) return;

    lastProps.current = props;
    props$.current.next(props);
  });

  return element;
}
